import { inject, Injectable, Injector, InputSignal } from '@angular/core';
import { MentionsService } from '../../../../../services/chat-message/mentions/mentions.service';
import Mention from '@tiptap/extension-mention';
import { AngularRenderer } from 'ngx-tiptap';
import { MentionsList } from '../../mentions/mentions';
import tippy, { Instance } from 'tippy.js';
import type { SuggestionProps } from '@tiptap/suggestion';

export function createCustomMention(
  mentionsService: MentionsService,
  roomId: InputSignal<string>,
  rendererFactory: MentionRendererFactory
) {
  return Mention.extend({
    addAttributes() {
      return {
        id: { default: null },
        value: { default: null },
        displayname: { default: null },
        avatar: { default: null },
        link: { default: null },
        target: { default: '_blank' }
      };
    }
  }).configure({
    HTMLAttributes: {
      class: 'mention'
    },
    renderHTML({ node }) {
      const { value, displayname, link } = node.attrs;
      const safeHref = sanitizeMentionHref(link);
      const label = '@' + (displayname ?? value);

      if (!safeHref) {
        return ['span', { class: 'user_mention user_mention--unsafe' }, label];
      }

      return ['a', { class: 'user_mention', href: safeHref, target: '_blank', rel: 'noopener noreferrer' }, label];
    },
    suggestion: {
      char: '@',
      items: async ({ query }) => {
        return await mentionsService.mentionSourceHandler(roomId(), query.toLowerCase());
      },
      command: ({ editor, range, props }) => {
        const safeHref = sanitizeMentionHref(props.link);

        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            {
              type: 'mention',
              attrs: {
                id: props.id,
                value: props.value,
                displayname: props.displayname,
                avatar: props.avatar,
                link: safeHref,
                target: props.target
              }
            },
            { type: 'text', text: ' ' }
          ])
          .run();
      },
      render: () => {
        const renderer = rendererFactory.create();

        return {
          onStart: renderer.onStart,
          onUpdate: renderer.onUpdate,
          onKeyDown: ({ event }): boolean => renderer.onKeyDown(event),
          onExit: renderer.onExit
        };
      }
    }
  });
}

const ALLOWED_LINK_SCHEMES = new Set(['http:', 'https:', 'matrix:', 'mxc:']);

function sanitizeMentionHref(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  try {
    const url = new URL(raw, globalThis.location.origin);
    return ALLOWED_LINK_SCHEMES.has(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
}

export interface MentionRenderer {
  onStart(props: SuggestionProps): void;

  onUpdate(props: SuggestionProps): void;

  onKeyDown(event: KeyboardEvent): boolean;

  onExit(): void;
}

@Injectable({
  providedIn: 'root'
})
/**
 * Helferklasse für die Nutzung von Mentions im TipTap-Editor FactoryKlasse zum erstellen der Renderer
 *
 * Es wird folgende Anforderung in dieser Komponente erfüllt:
 * A_25437 - Erwähnungen
 * TI-M Clients MÜSSEN es ermöglichen, dass über das Eingabefeld andere Raumteilnehmer gemäß
 * [Client-Server API/#user-and-room-mentions] im jeweiligen Chatraum erwähnt werden können.
 * Dazu MUSS der TI-M Client eine entsprechende Nutzerliste anzeigen, sobald der Nutzer ein neues Wort mit "@" startet.
 */
export class MentionRendererFactory {
  private readonly injector = inject(Injector);

  create(): MentionRenderer {
    let renderer: AngularRenderer<MentionsList, MentionsList>;
    let popup: Instance | null = null;

    const getRect = (props: SuggestionProps): DOMRect => props.clientRect?.() ?? new DOMRect();

    return {
      onStart: (props): void => {
        if (!props.clientRect) return;

        renderer = new AngularRenderer(MentionsList, this.injector, { props });

        renderer.updateProps({ props });

        popup = tippy(document.body, {
          getReferenceClientRect: () => getRect(props),
          appendTo: () => document.body,
          content: renderer.dom,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'top-start'
        });
      },

      onUpdate: (props): void => {
        renderer.updateProps({ props });

        if (!popup || !props.clientRect) return;

        popup.setProps({
          getReferenceClientRect: () => getRect(props)
        });
      },

      onKeyDown: (event): boolean => {
        return renderer.instance.onKeyDown(event);
      },

      onExit: (): void => {
        popup?.destroy();
        renderer.destroy();
      }
    };
  }
}
